import { Component, ElementRef, Inject, OnDestroy, ViewChild, forwardRef } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform, base64ToFile } from 'ngx-image-cropper';
import { Subject } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { DocumentExtractDataService } from '../../services/document-extract-data.service';
import { ImageCompressService } from '../../utils/image-compress.service';
import { ToastService } from '../../toast/toast.service';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { environment } from 'src/environments/environment';

export interface LicenseUploadBottomSheetData {
  licenseType?: 'front' | 'back'; // Optional - combined mode doesn't use this
}

export interface LicenseUploadResult {
  frontImageFile: File;
  backImageFile: File;
  extractedData: any;
}

const FRONT_LICENSE_DEFAULT_NAME = 'front-license';
const BACK_LICENSE_DEFAULT_NAME = 'back-license';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB - matches backend limit
const VALID_IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

enum UploadState {
  FRONT_UPLOAD = 'front_upload',
  FRONT_CROPPING = 'front_cropping',
  EXTRACTING = 'extracting',
  BACK_UPLOAD = 'back_upload',
  BACK_CROPPING = 'back_cropping',
  EXTRACTION_COMPLETE = 'complete',
  EXTRACTION_ERROR = 'error'
}

@Component({
  selector: 'app-license-upload-bottom-sheet',
  templateUrl: './license-upload-bottom-sheet.component.html',
  styleUrls: ['./license-upload-bottom-sheet.component.scss'],
  standalone: false
})
export class LicenseUploadBottomSheetComponent implements OnDestroy {
  @ViewChild('frontCameraInput') frontCameraInput: ElementRef;
  @ViewChild('frontGalleryInput') frontGalleryInput: ElementRef;
  @ViewChild('backCameraInput') backCameraInput: ElementRef;
  @ViewChild('backGalleryInput') backGalleryInput: ElementRef;

  // State management
  currentState: UploadState = UploadState.FRONT_UPLOAD;
  UploadState = UploadState; // Expose enum to template

  // Front image states
  frontImageFile: File | null = null;
  frontImageUrl: SafeUrl | null = null;
  frontImageChangedEvent: any = null;
  frontLoading = false;
  frontImageFormat: string;

  // Back image states
  backImageFile: File | null = null;
  backImageUrl: SafeUrl | null = null;
  backImageChangedEvent: any = null;
  backLoading = false;
  backImageFormat: string;

  // Extraction states
  isExtracting = false;
  extractedData: any = null;
  extractionError = false;

  // Cropper properties (shared)
  canvasRotation = 0;
  transform: ImageTransform = {};
  cropperPosition = {
    x1: 100,
    y1: 100,
    x2: 460,
    y2: 260
  };

  acceptTypesPicture = VALID_IMAGE_TYPES.join(',');
  
  private destroy$ = new Subject<void>();

  constructor(
    private readonly bottomSheetRef: MatBottomSheetRef<LicenseUploadBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: LicenseUploadBottomSheetData,
    private readonly domSanitizer: DomSanitizer,
    private readonly imageCompressService: ImageCompressService,
    private readonly toastService: ToastService,
    @Inject(forwardRef(() => DocumentExtractDataService)) private readonly documentExtractDataService: DocumentExtractDataService,
    private readonly registrationService: RegistrationService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Front image actions
  openFrontCamera(): void {
    this.frontCameraInput?.nativeElement?.click();
  }

  openFrontGallery(): void {
    this.frontGalleryInput?.nativeElement?.click();
  }

  onProcessFrontImage(event: any): void {
    this.onProcessImage(event, 'front');
  }

  // Back image actions
  openBackCamera(): void {
    this.backCameraInput?.nativeElement?.click();
  }

  openBackGallery(): void {
    this.backGalleryInput?.nativeElement?.click();
  }

  onProcessBackImage(event: any): void {
    this.onProcessImage(event, 'back');
  }

  private onProcessImage(event: any, type: 'front' | 'back'): void {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;

      // Validate file type
      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        this.toastService.showError('Only JPEG, JPG, and PNG files are supported');
        return;
      }

      const imageFormat = this.getCropperFormat(file);

      if (type === 'front') {
        this.frontImageChangedEvent = event;
        this.frontImageFormat = imageFormat;
        this.frontLoading = true;
        this.currentState = UploadState.FRONT_CROPPING;
      } else {
        this.backImageChangedEvent = event;
        this.backImageFormat = imageFormat;
        this.backLoading = true;
        this.currentState = UploadState.BACK_CROPPING;
      }

      // Reset cropper transforms
      this.canvasRotation = 0;
      this.transform = {};
    }
  }

  async onFrontImageCropped(event: ImageCroppedEvent): Promise<void> {
    await this.onImageCropped(event, 'front');
  }

  async onBackImageCropped(event: ImageCroppedEvent): Promise<void> {
    await this.onImageCropped(event, 'back');
  }

  private async onImageCropped(event: ImageCroppedEvent, type: 'front' | 'back'): Promise<void> {
    if (event && (event.base64 || event.blob)) {
      const baseName = type === 'front' ? FRONT_LICENSE_DEFAULT_NAME : BACK_LICENSE_DEFAULT_NAME;
      const imageFormat = type === 'front' ? this.frontImageFormat : this.backImageFormat;
      const mimeType = this.getMimeType(event.blob?.type || imageFormat);
      const extension = this.getFileExtension(mimeType);
      const fileName = `${baseName}.${extension}`;

      try {
        let imageFile: File;

        if (event.blob) {
          imageFile = new File([event.blob], fileName, { type: mimeType });
        } else if (event.base64) {
          imageFile = new File([base64ToFile(event.base64)], fileName, { type: mimeType });
        }

        let fileToUse: File;
        try {
          fileToUse = await this.imageCompressService.compressImage(imageFile, 1);
        } catch (firstError) {
          console.warn('[License Upload] Compression attempt 1 failed, retrying:', firstError);
          try {
            // Brief delay to allow the browser to free memory before retrying
            await new Promise(resolve => setTimeout(resolve, 300));
            fileToUse = await this.imageCompressService.compressImage(imageFile, 1);
          } catch (retryError) {
            console.warn('[License Upload] Compression attempt 2 failed, using original file:', retryError);
            fileToUse = imageFile;
          }
        }

        // Wrap FileReader in a Promise to properly await it
        const url = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (readerEvent) => resolve(readerEvent.target.result as string);
          reader.onerror = () => reject(new Error('Failed to read processed image file'));
          reader.readAsDataURL(fileToUse);
        });
        
        if (type === 'front') {
          this.frontImageFile = fileToUse;
          this.frontImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
        } else {
          this.backImageFile = fileToUse;
          this.backImageUrl = this.domSanitizer.bypassSecurityTrustUrl(url);
        }
      } catch (error) {
        console.error('[License Upload] Error processing image:', error);
        this.toastService.showError('Unable to process image. Please try again.');
      }
    }
  }

  onFrontImageLoaded(): void {
    this.frontLoading = false;
  }

  private getCropperFormat(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const normalizedType = this.getMimeType(file.type);

    if (extension === 'jpg' || extension === 'jpeg' || normalizedType === 'image/jpeg') {
      return 'jpeg';
    }

    if (extension === 'png' || normalizedType === 'image/png') {
      return 'png';
    }

    return 'png';
  }

  private getMimeType(value?: string): string {
    const normalizedValue = value?.toLowerCase();

    if (!normalizedValue) {
      return 'image/png';
    }

    if (normalizedValue === 'jpg' || normalizedValue === 'jpeg' || normalizedValue === 'image/jpg' || normalizedValue === 'image/jpeg') {
      return 'image/jpeg';
    }

    if (normalizedValue === 'png' || normalizedValue === 'image/png') {
      return 'image/png';
    }

    return normalizedValue.includes('/') ? normalizedValue : 'image/png';
  }

  private getFileExtension(mimeType: string): string {
    if (mimeType === 'image/jpeg') {
      return 'jpg';
    }

    if (mimeType === 'image/png') {
      return 'png';
    }

    return 'png';
  }

  onBackImageLoaded(): void {
    this.backLoading = false;
  }

  onFrontLoadImageFailed(): void {
    this.toastService.showError('Failed to load front image. Please try again.');
    this.frontLoading = false;
    this.currentState = UploadState.FRONT_UPLOAD;
  }

  onBackLoadImageFailed(): void {
    this.toastService.showError('Failed to load back image. Please try again.');
    this.backLoading = false;
    this.currentState = this.frontImageFile ? UploadState.BACK_UPLOAD : UploadState.FRONT_UPLOAD;
  }

  // Cropper controls
  rotateLeft(): void {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight(): void {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  flipHorizontal(): void {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH
    };
  }

  flipVertical(): void {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV
    };
  }

  private flipAfterRotate(): void {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }

  // Confirm actions
  async onConfirmFront(): Promise<void> {
    // Double-check that the file exists before proceeding
    if (!this.frontImageFile) {
      this.toastService.showError('No image to save. Please try again.');
      return;
    }
    
    // Validate compressed file size against backend limit
    if (this.frontImageFile.size > MAX_FILE_SIZE) {
      this.toastService.showError(`Image file is too large (${(this.frontImageFile.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB. Please crop more of the image.`);
      return;
    }
    
    // Start extraction immediately
    this.currentState = UploadState.EXTRACTING;
    this.extractLicenseData(this.frontImageFile);
  }

  onConfirmBack(): void {
    if (!this.backImageFile) {
      this.toastService.showError('No image to save. Please try again.');
      return;
    }
    
    // Validate compressed file size against backend limit
    if (this.backImageFile.size > MAX_FILE_SIZE) {
      this.toastService.showError(`Image file is too large (${(this.backImageFile.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB. Please crop more of the image.`);
      return;
    }
    
    // Check if extraction already finished and determine final state
    if (this.extractedData && !this.isExtracting) {
      // Extraction succeeded, show success state
      this.currentState = UploadState.EXTRACTION_COMPLETE;
    } else if (this.extractionError && !this.isExtracting) {
      // Extraction failed, show error state
      this.currentState = UploadState.EXTRACTION_ERROR;
    } else if (this.isExtracting) {
      // Still extracting, wait for it to complete
      this.currentState = UploadState.EXTRACTING;
    } else {
      // Extraction hasn't started (shouldn't happen normally)
      this.currentState = UploadState.BACK_UPLOAD;
    }
  }

  // Cancel actions
  onCancelFront(): void {
    this.frontImageFile = null;
    this.frontImageUrl = null;
    this.frontImageChangedEvent = null;
    this.currentState = UploadState.FRONT_UPLOAD;
  }

  onCancelBack(): void {
    this.backImageFile = null;
    this.backImageUrl = null;
    this.backImageChangedEvent = null;
    this.currentState = this.isExtracting ? UploadState.EXTRACTING : UploadState.BACK_UPLOAD;
  }

  onCancel(): void {
    this.bottomSheetRef.dismiss(null);
  }

  onContinue(): void {
    if (!this.frontImageFile || !this.backImageFile) {
      this.toastService.showError('Please upload both front and back license images.');
      return;
    }

    const result: LicenseUploadResult = {
      frontImageFile: this.frontImageFile,
      backImageFile: this.backImageFile,
      extractedData: this.extractedData
    };

    this.bottomSheetRef.dismiss(result);
  }

  /**
   * Extract license data using Document Extract API
   */
  private extractLicenseData(file: File): void {
    this.isExtracting = true;
    this.extractionError = false;

    // Format instructions to guide the LLM on how to format the extracted data
    const formatInstructions = {
      dates: 'All dates MUST be in YYYY-MM-DD format (e.g., 1990-05-15, 2022-08-02)',
      address: 'Parse address into ALL components: address_street (street address only), address_city (city name), address_subdivision (2-letter province/state code), address_postal_code (postal/zip code), and address_country (FULL country name). Also provide full_address as complete formatted address.',
      address_country: 'Return address_country as FULL country name (Canada or USA, not CA or US)',
      issuing_country: 'Return issuing_country as abbreviated lowercase code (ca for Canada, us for USA)',
      subdivision: 'Return address_subdivision as 2-letter code without country prefix (ON, BC, NY, CA - NOT US-NY or CA-ON)',
      gender: 'Return gender as lowercase: "male" or "female" only'
    };

    this.documentExtractDataService
      .extractDocument(file, 'DriverLicense', undefined, undefined, formatInstructions)
      .pipe(
        first(),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isExtracting = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.extractedData = response;
            this.registrationService.storeExtractedLicenseData(response);
            
            // Only transition to complete state if back image is already confirmed
            // Check both file exists AND we're not currently cropping it
            if (this.backImageFile && this.currentState !== UploadState.BACK_CROPPING) {
              this.currentState = UploadState.EXTRACTION_COMPLETE;
            } else {
              // Back image not ready yet, stay in current state (EXTRACTING or BACK_UPLOAD)
              // When user confirms back image, onConfirmBack() will transition to EXTRACTION_COMPLETE
              if (this.currentState === UploadState.EXTRACTING) {
                this.currentState = UploadState.BACK_UPLOAD;
              }
            }
          } else {
            // Extraction returned no data
            this.extractionError = true;
            // Only show error state if back image is already confirmed
            if (this.backImageFile && this.currentState !== UploadState.BACK_CROPPING) {
              this.currentState = UploadState.EXTRACTION_ERROR;
            } else {
              // Wait for back image to be confirmed
              if (this.currentState === UploadState.EXTRACTING) {
                this.currentState = UploadState.BACK_UPLOAD;
              }
            }
          }
        },
        error: (error) => {
          console.error('[License Upload] Extraction error:', error);
          this.extractedData = null;
          this.extractionError = true;
          // Only show error state if back image is already confirmed
          if (this.backImageFile && this.currentState !== UploadState.BACK_CROPPING) {
            this.currentState = UploadState.EXTRACTION_ERROR;
          } else {
            // Wait for back image to be confirmed
            if (this.currentState === UploadState.EXTRACTING) {
              this.currentState = UploadState.BACK_UPLOAD;
            }
          }
        }
      });
  }

  get isBackEnabled(): boolean {
    return !!this.frontImageFile && this.currentState !== UploadState.FRONT_CROPPING;
  }
}

